import { useState, useCallback, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { useTranslation } from '@/i18n';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

export function ChatInput({ onSend, disabled = false }: ChatInputProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (!value.trim() || disabled) return;
      onSend(value.trim());
      setValue('');
      // Reset textarea height
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    },
    [value, disabled, onSend],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  return (
    <div className="border-t border-gray-100 bg-white safe-bottom">
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto flex items-end gap-2 p-3 md:p-4"
      >
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={t('chat.placeholder')}
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none bg-brand-cream/50 rounded-2xl px-4 py-3
            text-[15px] text-brand-charcoal placeholder:text-brand-charcoal/40
            focus:outline-none focus:ring-2 focus:ring-brand-gold/30
            disabled:opacity-50 transition-all
            max-h-[120px]"
          aria-label="Message input"
        />
        <button
          type="submit"
          disabled={!value.trim() || disabled}
          className="flex-shrink-0 w-11 h-11 rounded-full bg-brand-navy
            flex items-center justify-center
            text-white hover:bg-brand-charcoal
            disabled:opacity-30 disabled:hover:bg-brand-navy
            active:scale-95 transition-all"
          aria-label="Send message"
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}
